import React, { useState, useEffect } from 'react';
import { Users, TrendingUp, Clock, CreditCard, ChevronRight, TrendingDown, Filter, X, ToggleLeft, ToggleRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
const SegmentOverview = ({
  onSelectSegment,
  filters,
  onFilterChange,
  onResetFilters,
  customerSatisfaction = {
    urban: 92,
    highway: 85,
    rural: 89,
    suburban: 87
  },
  clvData = {
    urban: 8.7,
    highway: 6.4,
    rural: 5.3,
    suburban: 7.1
  },
  shareOfWalletData = {
    urban: 80,
    highway: 85,
    rural: 54,
    suburban: 65
  }
}) => {
  const [selectedMetric, setSelectedMetric] = useState('volume');
  const [filteredSegments, setFilteredSegments] = useState([]);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [selectedFuelType, setSelectedFuelType] = useState('all');
  const [showFrequency, setShowFrequency] = useState(false);
  const segments = [{
    id: 'urban',
    name: 'Urban Premium',
    stations: 523,
    color: '#8B5CF6',
    region: ['bangkok', 'central'],
    format: ['flagship', 'standard'],
    performance: 'top',
    growth: 'growing',
    kpis: {
      volume: 105,
      shareOfWallet: shareOfWalletData.urban,
      traffic: 847,
      conversion: 68,
      basketSize: 752,
      clv: clvData.urban,
      satisfaction: customerSatisfaction.urban
    }
  }, {
    id: 'highway',
    name: 'Highway Transit',
    stations: 682,
    color: '#3B82F6',
    region: ['central', 'eastern', 'northern'],
    format: ['standard'],
    performance: 'top',
    growth: 'growing',
    kpis: {
      volume: 95,
      shareOfWallet: shareOfWalletData.highway,
      traffic: 925,
      conversion: 52,
      basketSize: 820,
      clv: clvData.highway,
      satisfaction: customerSatisfaction.highway
    }
  }, {
    id: 'rural',
    name: 'Rural Standard',
    stations: 754,
    color: '#10B981',
    region: ['northern', 'southern', 'eastern'],
    format: ['standard'],
    performance: 'bottom',
    growth: 'declining',
    kpis: {
      volume: 88,
      shareOfWallet: shareOfWalletData.rural,
      traffic: 625,
      conversion: 58,
      basketSize: 580,
      clv: clvData.rural,
      satisfaction: customerSatisfaction.rural
    }
  }, {
    id: 'suburban',
    name: 'Suburban Mixed',
    stations: 384,
    color: '#F59E0B',
    region: ['bangkok', 'central'],
    format: ['flagship', 'standard'],
    performance: 'bottom',
    growth: 'declining',
    kpis: {
      volume: 84,
      shareOfWallet: shareOfWalletData.suburban,
      traffic: 725,
      conversion: 62,
      basketSize: 650,
      clv: clvData.suburban,
      satisfaction: customerSatisfaction.suburban
    }
  }];
  useEffect(() => {
    let result = [...segments];
    if (filters.region !== 'all') {
      result = result.filter(segment => segment.region.includes(filters.region));
    }
    if (filters.format !== 'all') {
      result = result.filter(segment => segment.format.includes(filters.format));
    }
    if (filters.performance !== 'all') {
      result = result.filter(segment => segment.performance === filters.performance);
    }
    if (filters.growth !== 'all') {
      result = result.filter(segment => segment.growth === filters.growth);
    }
    setFilteredSegments(result);
  }, [filters]);
  const getChartData = () => {
    let multiplier = 1.0;
    if (selectedMetric === 'volume' && selectedFuelType !== 'all') {
      switch (selectedFuelType) {
        case 'petrol':
          multiplier = 0.6; // 60% petrol
          break;
        case 'diesel':
          multiplier = 0.4; // 40% diesel
          break;
        case 'premium':
          multiplier = 0.15; // 15% premium fuels
          break;
        case 'e20':
          multiplier = 0.12; // 12% E20
          break;
        case 'e85':
          multiplier = 0.08; // 8% E85
          break;
        case 'ev':
          multiplier = 0.05; // 5% EV charging
          break;
        case 'lpg':
          multiplier = 0.2; // 20% LPG
          break;
        case 'ngv':
          multiplier = 0.1; // 10% NGV
          break;
        default:
          multiplier = 1.0;
      }
    }
    if (selectedMetric === 'volume') {
      return [{
        month: 'Jan',
        urban: Math.round(101 * multiplier),
        highway: Math.round(99 * multiplier),
        rural: Math.round(96 * multiplier),
        suburban: Math.round(90 * multiplier)
      }, {
        month: 'Feb',
        urban: Math.round(103 * multiplier),
        highway: Math.round(98 * multiplier),
        rural: Math.round(94 * multiplier),
        suburban: Math.round(91 * multiplier)
      }, {
        month: 'Mar',
        urban: Math.round(105 * multiplier),
        highway: Math.round(97 * multiplier),
        rural: Math.round(92 * multiplier),
        suburban: Math.round(92 * multiplier)
      }, {
        month: 'Apr',
        urban: Math.round(106 * multiplier),
        highway: Math.round(96 * multiplier),
        rural: Math.round(90 * multiplier),
        suburban: Math.round(90 * multiplier)
      }, {
        month: 'May',
        urban: Math.round(108 * multiplier),
        highway: Math.round(95 * multiplier),
        rural: Math.round(89 * multiplier),
        suburban: Math.round(89 * multiplier)
      }, {
        month: 'Jun',
        urban: Math.round(110 * multiplier),
        highway: Math.round(95 * multiplier),
        rural: Math.round(88 * multiplier),
        suburban: Math.round(88 * multiplier)
      }];
    } else if (selectedMetric === 'shareOfWallet') {
      return [{
        month: 'Jan',
        urban: 78,
        highway: 83,
        rural: 52,
        suburban: 63
      }, {
        month: 'Feb',
        urban: 79,
        highway: 84,
        rural: 53,
        suburban: 64
      }, {
        month: 'Mar',
        urban: 79,
        highway: 84,
        rural: 53,
        suburban: 64
      }, {
        month: 'Apr',
        urban: 80,
        highway: 85,
        rural: 54,
        suburban: 65
      }, {
        month: 'May',
        urban: 80,
        highway: 85,
        rural: 54,
        suburban: 65
      }, {
        month: 'Jun',
        urban: 80,
        highway: 85,
        rural: 54,
        suburban: 65
      }];
    } else if (selectedMetric === 'traffic') {
      return [{
        month: 'Jan',
        urban: 820,
        highway: 915,
        rural: 605,
        suburban: 710
      }, {
        month: 'Feb',
        urban: 825,
        highway: 920,
        rural: 610,
        suburban: 715
      }, {
        month: 'Mar',
        urban: 830,
        highway: 925,
        rural: 615,
        suburban: 720
      }, {
        month: 'Apr',
        urban: 835,
        highway: 930,
        rural: 620,
        suburban: 725
      }, {
        month: 'May',
        urban: 840,
        highway: 935,
        rural: 625,
        suburban: 730
      }, {
        month: 'Jun',
        urban: 845,
        highway: 940,
        rural: 630,
        suburban: 735
      }];
    } else if (selectedMetric === 'conversion') {
      return [{
        month: 'Jan',
        urban: 63,
        highway: 47,
        rural: 53,
        suburban: 57
      }, {
        month: 'Feb',
        urban: 64,
        highway: 48,
        rural: 54,
        suburban: 58
      }, {
        month: 'Mar',
        urban: 65,
        highway: 49,
        rural: 55,
        suburban: 59
      }, {
        month: 'Apr',
        urban: 66,
        highway: 50,
        rural: 56,
        suburban: 60
      }, {
        month: 'May',
        urban: 67,
        highway: 51,
        rural: 57,
        suburban: 61
      }, {
        month: 'Jun',
        urban: 68,
        highway: 52,
        rural: 58,
        suburban: 62
      }];
    } else if (selectedMetric === 'clv') {
      if (showFrequency) {
        return getFrequencyData();
      }
      return [{
        year: '2018',
        urban: 7.2,
        highway: 5.8,
        rural: 4.9,
        suburban: 6.2
      }, {
        year: '2019',
        urban: 7.6,
        highway: 6.1,
        rural: 5.0,
        suburban: 6.5
      }, {
        year: '2020',
        urban: 7.9,
        highway: 6.3,
        rural: 5.1,
        suburban: 6.7
      }, {
        year: '2021',
        urban: 8.2,
        highway: 6.4,
        rural: 5.2,
        suburban: 6.9
      }, {
        year: '2022',
        urban: 8.5,
        highway: 6.4,
        rural: 5.3,
        suburban: 7.0
      }, {
        year: '2023',
        urban: clvData.urban,
        highway: clvData.highway,
        rural: clvData.rural,
        suburban: clvData.suburban
      }];
    } else if (selectedMetric === 'basketSize') {
      return [{
        month: 'Jan',
        urban: 730,
        highway: 800,
        rural: 560,
        suburban: 630
      }, {
        month: 'Feb',
        urban: 735,
        highway: 805,
        rural: 565,
        suburban: 635
      }, {
        month: 'Mar',
        urban: 740,
        highway: 810,
        rural: 570,
        suburban: 640
      }, {
        month: 'Apr',
        urban: 745,
        highway: 815,
        rural: 575,
        suburban: 645
      }, {
        month: 'May',
        urban: 750,
        highway: 820,
        rural: 580,
        suburban: 650
      }, {
        month: 'Jun',
        urban: 755,
        highway: 825,
        rural: 585,
        suburban: 655
      }];
    }
    return [];
  };
  const getFrequencyData = () => {
    return [{
      year: '2018',
      urban: 35,
      highway: 15,
      rural: 19,
      suburban: 32
    }, {
      year: '2019',
      urban: 36,
      highway: 17,
      rural: 20,
      suburban: 35
    }, {
      year: '2020',
      urban: 35,
      highway: 18,
      rural: 21,
      suburban: 40
    }, {
      year: '2021',
      urban: 40,
      highway: 17,
      rural: 26,
      suburban: 45
    }, {
      year: '2022',
      urban: 45,
      highway: 20,
      rural: 24,
      suburban: 50
    }, {
      year: '2023',
      urban: 50,
      highway: 24,
      rural: 22,
      suburban: 56
    }];
  };
  const trendData = getChartData();
  const getYAxisLabel = () => {
    switch (selectedMetric) {
      case 'volume':
        return 'Volume Index';
      case 'shareOfWallet':
        return 'Share of Wallet (%)';
      case 'traffic':
        return 'Daily Traffic';
      case 'conversion':
        return 'Conversion Rate (%)';
      case 'basketSize':
        return 'Basket Size (฿)';
      case 'clv':
        return showFrequency ? 'Average Annual Frequency (#)' : 'CLV (x)';
      default:
        return '';
    }
  };
  const getProductLabel = () => {
    switch (selectedFuelType) {
      case 'petrol':
        return 'Petrol Only';
      case 'diesel':
        return 'Diesel Only';
      case 'premium':
        return 'Premium Fuels';
      case 'e20':
        return 'E20 Gasohol';
      case 'e85':
        return 'E85 Gasohol';
      case 'ev':
        return 'EV Charging';
      case 'lpg':
        return 'LPG';
      case 'ngv':
        return 'NGV (Natural Gas)';
      case 'all':
      default:
        return 'All Products';
    }
  };
  const demographicData = [{
    name: 'Male',
    value: 58
  }, {
    name: 'Female',
    value: 42
  }];
  const ageData = [{
    name: '18-24',
    value: 12
  }, {
    name: '25-34',
    value: 28
  }, {
    name: '35-44',
    value: 24
  }, {
    name: '45-54',
    value: 18
  }, {
    name: '55+',
    value: 18
  }];
  const peakHoursData = [{
    hour: '6-8am',
    urban: 85,
    highway: 80,
    rural: 95,
    suburban: 90
  }, {
    hour: '8-10am',
    urban: 92,
    highway: 88,
    rural: 85,
    suburban: 100
  }, {
    hour: '10am-12pm',
    urban: 78,
    highway: 92,
    rural: 75,
    suburban: 75
  }, {
    hour: '12-2pm',
    urban: 82,
    highway: 100,
    rural: 72,
    suburban: 70
  }, {
    hour: '2-4pm',
    urban: 75,
    highway: 95,
    rural: 70,
    suburban: 65
  }, {
    hour: '4-6pm',
    urban: 95,
    highway: 90,
    rural: 80,
    suburban: 85
  }, {
    hour: '6-8pm',
    urban: 100,
    highway: 85,
    rural: 90,
    suburban: 95
  }, {
    hour: '8-10pm',
    urban: 88,
    highway: 75,
    rural: 65,
    suburban: 75
  }];
  const COLORS = ['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B'];
  const regions = [{
    id: 'all',
    name: 'All Regions'
  }, {
    id: 'bangkok',
    name: 'Bangkok'
  }, {
    id: 'central',
    name: 'Central'
  }, {
    id: 'northern',
    name: 'Northern'
  }, {
    id: 'southern',
    name: 'Southern'
  }, {
    id: 'eastern',
    name: 'Eastern'
  }];
  const formats = [{
    id: 'all',
    name: 'All Formats'
  }, {
    id: 'standard',
    name: 'Standard'
  }, {
    id: 'flagship',
    name: 'Flagship'
  }];
  const performances = [{
    id: 'all',
    name: 'All Performance'
  }, {
    id: 'top',
    name: 'Top Performers'
  }, {
    id: 'bottom',
    name: 'Bottom Performers'
  }];
  const growths = [{
    id: 'all',
    name: 'All Growth'
  }, {
    id: 'growing',
    name: 'Growing'
  }, {
    id: 'declining',
    name: 'Declining'
  }];
  return <div className="p-6">
      <div className="mb-6">
        <h1 className="font-semibold text-gray-900 mb-2 text-lg">Segmentation Analysis</h1>
        <p className="text-gray-600">
      </p>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Filter size={18} className="mr-2 text-purple-600" />
            Filters
          </h3>
          <button className="text-sm text-purple-700 hover:text-purple-900 font-medium" onClick={() => setShowFilterPanel(!showFilterPanel)}>
            {showFilterPanel ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>

        {showFilterPanel && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
              <select value={filters.region} onChange={e => onFilterChange('region', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                {regions.map(region => <option key={region.id} value={region.id}>{region.name}</option>)}
              </select>
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
              <select value={filters.format} onChange={e => onFilterChange('format', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                {formats.map(format => <option key={format.id} value={format.id}>{format.name}</option>)}
              </select>
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">Performance</label>
              <select value={filters.performance} onChange={e => onFilterChange('performance', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                {performances.map(performance => <option key={performance.id} value={performance.id}>{performance.name}</option>)}
              </select>
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">Growth</label>
              <select value={filters.growth} onChange={e => onFilterChange('growth', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                {growths.map(growth => <option key={growth.id} value={growth.id}>{growth.name}</option>)}
              </select>
            </div>
          </div>}

        {Object.values(filters).some(v => v !== 'all') && <div className="flex flex-wrap gap-2">
            <div className="mr-2 text-sm font-medium text-gray-700">Active filters:</div>
            
            {filters.region !== 'all' && <div className="bg-purple-50 px-3 py-1 rounded-full text-xs flex items-center">
                <span className="font-medium text-purple-800 mr-1">Region:</span>
                <span className="text-purple-700">
                  {regions.find(r => r.id === filters.region)?.name}
                </span>
                <X size={14} className="ml-2 text-purple-700 cursor-pointer hover:text-purple-900" onClick={() => onFilterChange('region', 'all')} />
              </div>}
            
            {filters.format !== 'all' && <div className="bg-purple-50 px-3 py-1 rounded-full text-xs flex items-center">
                <span className="font-medium text-purple-800 mr-1">Format:</span>
                <span className="text-purple-700">
                  {formats.find(f => f.id === filters.format)?.name}
                </span>
                <X size={14} className="ml-2 text-purple-700 cursor-pointer hover:text-purple-900" onClick={() => onFilterChange('format', 'all')} />
              </div>}
            
            {filters.performance !== 'all' && <div className="bg-purple-50 px-3 py-1 rounded-full text-xs flex items-center">
                <span className="font-medium text-purple-800 mr-1">Performance:</span>
                <span className="text-purple-700">
                  {performances.find(p => p.id === filters.performance)?.name}
                </span>
                <X size={14} className="ml-2 text-purple-700 cursor-pointer hover:text-purple-900" onClick={() => onFilterChange('performance', 'all')} />
              </div>}
            
            {filters.growth !== 'all' && <div className="bg-purple-50 px-3 py-1 rounded-full text-xs flex items-center">
                <span className="font-medium text-purple-800 mr-1">Growth:</span>
                <span className="text-purple-700">
                  {growths.find(g => g.id === filters.growth)?.name}
                </span>
                <X size={14} className="ml-2 text-purple-700 cursor-pointer hover:text-purple-900" onClick={() => onFilterChange('growth', 'all')} />
              </div>}
            
            <button className="ml-auto text-xs font-medium px-3 py-1 rounded bg-purple-700 text-white hover:bg-purple-800" onClick={onResetFilters}>
              Reset Filters
            </button>
          </div>}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {filteredSegments.length > 0 ? filteredSegments.map(segment => <div key={segment.id} className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-purple-200" onClick={() => onSelectSegment(segment.id)}>
              <div className="flex justify-between items-start mb-3">
                <div className="p-2 rounded-md" style={{
            backgroundColor: `${segment.color}20`
          }}>
                  <Users size={18} style={{
              color: segment.color
            }} />
                </div>
                <div className="flex items-center">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full mr-2 ${segment.growth === 'growing' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {segment.growth === 'growing' ? <span className="flex items-center">
                        <TrendingUp size={12} className="mr-1" />
                        Growing
                      </span> : <span className="flex items-center">
                        <TrendingDown size={12} className="mr-1" />
                        Declining
                      </span>}
                  </span>
                  <span className="text-sm text-gray-500">{segment.stations} stations</span>
                </div>
              </div>
              
              <h3 className="text-lg font-medium text-gray-900 mb-4">{segment.name}</h3>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Volume Index</span>
                  <span className={`text-sm font-medium ${segment.kpis.volume > 100 ? 'text-green-600' : segment.kpis.volume > 90 ? 'text-gray-900' : 'text-red-600'}`}>{segment.kpis.volume}%</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Share of Wallet</span>
                  <span className={`text-sm font-medium ${segment.kpis.shareOfWallet > 75 ? 'text-green-600' : segment.kpis.shareOfWallet > 60 ? 'text-gray-900' : 'text-red-600'}`}>{segment.kpis.shareOfWallet}%</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">CLV</span>
                  <span className={`text-sm font-medium ${segment.kpis.clv > 7.5 ? 'text-green-600' : segment.kpis.clv > 6 ? 'text-gray-900' : 'text-red-600'}`}>{segment.kpis.clv.toFixed(1)}x</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Customer Satisfaction</span>
                  <span className={`text-sm font-medium ${segment.kpis.satisfaction > 90 ? 'text-green-600' : segment.kpis.satisfaction > 85 ? 'text-gray-900' : 'text-red-600'}`}>{segment.kpis.satisfaction}%</span>
                </div>
              </div>
              
              <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center">
                <span className="text-sm font-medium text-blue-600">View Details</span>
                <ChevronRight size={16} className="text-blue-600" />
              </div>
            </div>) : <div className="col-span-4 p-6 bg-white rounded-lg shadow text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">No segments match the current filters</h3>
            <p className="text-gray-600">Try adjusting your filter criteria to see results.</p>
          </div>}
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="lg:col-span-2 flex justify-start mb-2">
          <div className="flex flex-wrap space-x-2 bg-gray-100 p-1 rounded-md">
            <button onClick={() => setSelectedMetric('volume')} className={`px-3 py-1 rounded-md text-sm font-medium ${selectedMetric === 'volume' ? 'bg-white shadow text-blue-700' : 'text-gray-600'}`}>
              Volume
            </button>
            <button onClick={() => setSelectedMetric('shareOfWallet')} className={`px-3 py-1 rounded-md text-sm font-medium ${selectedMetric === 'shareOfWallet' ? 'bg-white shadow text-blue-700' : 'text-gray-600'}`}>
              Share of Wallet
            </button>
            <button onClick={() => setSelectedMetric('traffic')} className={`px-3 py-1 rounded-md text-sm font-medium ${selectedMetric === 'traffic' ? 'bg-white shadow text-blue-700' : 'text-gray-600'}`}>
              Traffic
            </button>
            <button onClick={() => setSelectedMetric('conversion')} className={`px-3 py-1 rounded-md text-sm font-medium ${selectedMetric === 'conversion' ? 'bg-white shadow text-blue-700' : 'text-gray-600'}`}>
              Conversion
            </button>
            <button onClick={() => setSelectedMetric('basketSize')} className={`px-3 py-1 rounded-md text-sm font-medium ${selectedMetric === 'basketSize' ? 'bg-white shadow text-blue-700' : 'text-gray-600'}`}>
              Basket Size
            </button>
            <button onClick={() => setSelectedMetric('clv')} className={`px-3 py-1 rounded-md text-sm font-medium ${selectedMetric === 'clv' ? 'bg-white shadow text-blue-700' : 'text-gray-600'}`}>
              CLV
            </button>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <TrendingUp size={20} className="mr-2 text-blue-600" />
              {selectedMetric === 'volume' && 'Volume Performance Trend'}
              {selectedMetric === 'shareOfWallet' && 'Share of Wallet Performance Trend'}
              {selectedMetric === 'traffic' && 'Traffic Performance Trend'}
              {selectedMetric === 'conversion' && 'Conversion Rate Trend'}
              {selectedMetric === 'basketSize' && 'Basket Size Trend'}
              {selectedMetric === 'clv' && (showFrequency ? 'Annual Visit Frequency Trend' : 'Customer Lifetime Value Trend')}
            </h3>
            
            {selectedMetric === 'volume' && <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500">Product:</span>
                <select className="bg-white border border-gray-300 rounded-md px-2 py-1 text-xs" value={selectedFuelType} onChange={e => setSelectedFuelType(e.target.value)}>
                  <option value="all">All Products</option>
                  <option value="petrol">Petrol</option>
                  <option value="diesel">Diesel</option>
                  <option value="premium">Premium Fuels</option>
                  <option value="e20">E20 Gasohol</option>
                  <option value="e85">E85 Gasohol</option>
                  <option value="lpg">LPG</option>
                  <option value="ngv">NGV</option>
                  <option value="ev">EV Charging</option>
                </select>
              </div>}
            
            {selectedMetric === 'clv' && <div className="flex items-center space-x-2 cursor-pointer" onClick={() => setShowFrequency(!showFrequency)}>
                <span className="text-xs text-gray-500">
                  {showFrequency ? "Show CLV" : "Show Frequency"}
                </span>
                <div className="relative">
                  {showFrequency ? <ToggleRight size={20} className="text-blue-600" /> : <ToggleLeft size={20} className="text-gray-400" />}
                </div>
              </div>}
          </div>
          
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              {selectedMetric === 'clv' ? <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis label={{
                value: getYAxisLabel(),
                angle: -90,
                position: 'insideLeft'
              }} />
                  <Tooltip />
                  <Legend />
                  {filteredSegments.map(segment => <Line key={segment.id} type="monotone" dataKey={segment.id} name={segment.name} stroke={segment.color} strokeWidth={2} activeDot={{
                r: 8
              }} />)}
                </LineChart> : <BarChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis label={{
                value: getYAxisLabel(),
                angle: -90,
                position: 'insideLeft'
              }} />
                  <Tooltip />
                  <Legend />
                  {filteredSegments.map(segment => <Bar key={segment.id} dataKey={segment.id} name={segment.name} fill={segment.color} />)}
                </BarChart>}
            </ResponsiveContainer>
          </div>
          
          {selectedMetric === 'clv' && <div className="mt-2 text-xs text-gray-500 text-center">
              Showing data for: {showFrequency ? "Average Annual Visit Frequency" : "Customer Lifetime Value"}
            </div>}
          
          {selectedMetric === 'volume' && selectedFuelType !== 'all' && <div className="mt-2 text-xs text-gray-500 text-center">
              Showing data for: {getProductLabel()}
            </div>}
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Clock size={20} className="mr-2 text-blue-600" />
            Peak Hours Transaction Pattern
          </h3>
          
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={peakHoursData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip />
                <Legend />
                {filteredSegments.map(segment => <Bar key={segment.id} dataKey={segment.id} name={segment.name} fill={segment.color} />)}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Users size={20} className="mr-2 text-blue-600" />
          Demographic Overview
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="h-64">
            <h4 className="text-md font-medium text-gray-700 mb-2 text-center">Gender Distribution</h4>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={demographicData} cx="50%" cy="50%" labelLine={true} label={({
                name,
                percent
              }) => `${name} (${(percent * 100).toFixed(0)}%)`} outerRadius={80} fill="#8884d8" dataKey="value">
                  {demographicData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div className="h-64">
            <h4 className="text-md font-medium text-gray-700 mb-2 text-center">Age Distribution</h4>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={ageData} cx="50%" cy="50%" labelLine={true} label={({
                name,
                percent
              }) => `${name} (${(percent * 100).toFixed(0)}%)`} outerRadius={80} fill="#8884d8" dataKey="value">
                  {ageData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>;
};
export default SegmentOverview;